import ReactPixel from "react-facebook-pixel";
import { initGA } from "./ga";
import ReactGTM from "react-gtm-module";
import type { Configuration } from "../../app/models/Configuration";

/** Sztywniak per obiekt — zostaje jako fallback gdy API nie zwraca jeszcze pól analityki */
type LegacyFacilityAnalyticsRow = { pixelId: string; gaId?: string; GTM?: string };

const getAnaliticsConfigByFacility: Record<string, LegacyFacilityAnalyticsRow> = {
  "273": { pixelId: "1232725160738501", gaId: "G-NRHETLWQFK" },
  "1224": { pixelId: "913533666380507", GTM: "GTM-WJBCZSD" },
  "1204": { pixelId: "777232707357603" },
  "1200": { pixelId: "1637422596662010" },
  "1227": { pixelId: "885648790025244", gaId: "G-Q16RVEQD1D" },
  "1214": { pixelId: "", GTM: "GTM-T4LHX4S" },
  "1240": { pixelId: "", gaId: "G-5YRNL6YTS5" },
  "1241": { pixelId: "1737692646444566", GTM: "GTM-NZT9RJ" },
  "1243": { pixelId: "1737692646444566", GTM: "GTM-NZT9RJ" },
  "1212": { pixelId: "", GTM: "GTM-NFXDL5WJ" },
  "1196": { pixelId: "", GTM: "GTM-MFSPN897" },
  "1253": { pixelId: "1518446349936606", GTM: "GTM-PFGLPWGX" },
};

export type ResolvedFacilityAnalytics = {
  pixelId?: string;
  gaId?: string;
  GTM?: string;
};

const advancedMatching = { ct: "", country: "", db: "", em: "", fn: "", ge: "", ln: "", ph: "", st: "", zp: "" };

const options = {
  autoConfig: true,
  debug: false,
};

function trimOrUndefined(value: string | null | undefined): string | undefined {
  if (value == null) return undefined;
  const t = value.trim();
  return t || undefined;
}

/**
 * Niepusty tag z konfiguracji (API) wygrywa.
 * Pusty string / null / brak pola — traktujemy jak „jeszcze nie wpisano”: wtedy obowiązuje sztywniak (jeśli jest).
 */
function resolveAnalyticsField(
  fromConfig: string | null | undefined,
  fallbackRaw: string | undefined,
): string | undefined {
  const fromApi = trimOrUndefined(fromConfig);
  if (fromApi) return fromApi;
  return trimOrUndefined(fallbackRaw);
}

function getFallbackAnalytics(facilityId: string): ResolvedFacilityAnalytics | undefined {
  const row = getAnaliticsConfigByFacility[facilityId];
  if (!row) return undefined;
  return {
    pixelId: trimOrUndefined(row.pixelId),
    gaId: trimOrUndefined(row.gaId),
    GTM: trimOrUndefined(row.GTM),
  };
}

/** Łączy tagi z konfiguracji sklepu ze sztywniakiem (wsteczna kompatybilność). */
export function resolveAnalyticsTags(
  configuration: Configuration | null | undefined,
  facilityId: string,
): ResolvedFacilityAnalytics {
  const fallback = getFallbackAnalytics(facilityId);
  return {
    pixelId: resolveAnalyticsField(configuration?.fbPixelTag, fallback?.pixelId),
    gaId: resolveAnalyticsField(configuration?.gaAnaliticsTag, fallback?.gaId),
    GTM: resolveAnalyticsField(configuration?.gtmAnaliticsTag, fallback?.GTM),
  };
}

let cachedAnalytics: { facilityId: string; tags: ResolvedFacilityAnalytics } | null = null;

/** Zapobiega wielokrotnemu ReactPixel.init / initGA / GTM przy ponownych renderach (ta sama konfiguracja, nowa referencja z Redux). */
let lastInitializedPixelId: string | undefined;
let lastInitializedGaId: string | undefined;
let lastInitializedGtmId: string | undefined;

function getAnalyticsTagsForFacility(facilityId: string): ResolvedFacilityAnalytics {
  if (cachedAnalytics?.facilityId === facilityId) {
    return cachedAnalytics.tags;
  }
  return resolveAnalyticsTags(undefined, facilityId);
}

export function initializeAnaliticsByFacility(facilityIds: string, configuration?: Configuration | null): void {
  if (!facilityIds) return;

  const tags = resolveAnalyticsTags(configuration ?? undefined, facilityIds);
  cachedAnalytics = { facilityId: facilityIds, tags };

  const hasAnyTag = !!(tags.pixelId || tags.gaId || tags.GTM);
  if (!hasAnyTag) {
    return;
  }

  let didRunNewInit = false;
  const startedChannels: string[] = [];

  if (tags.pixelId && tags.pixelId !== lastInitializedPixelId) {
    ReactPixel.init(tags.pixelId, advancedMatching, options);
    lastInitializedPixelId = tags.pixelId;
    didRunNewInit = true;
    startedChannels.push(`Meta Pixel (${tags.pixelId})`);
  }

  if (tags.gaId && tags.gaId !== lastInitializedGaId) {
    initGA(tags.gaId);
    lastInitializedGaId = tags.gaId;
    didRunNewInit = true;
    startedChannels.push(`Google Analytics (${tags.gaId})`);
  }

  if (tags.GTM && tags.GTM !== lastInitializedGtmId) {
    ReactGTM.initialize({ gtmId: tags.GTM });
    lastInitializedGtmId = tags.GTM;
    didRunNewInit = true;
    startedChannels.push(`GTM (${tags.GTM})`);
  }

  if (didRunNewInit) {
    console.info(`Analytics has been successfully initialized — ${startedChannels.join(", ")}`);
  }
}

export const canSendToGA = (facilityId: string): boolean => {
  return !!getAnalyticsTagsForFacility(facilityId).gaId;
};

export const canSendToFacebookPixel = (facilityId: string): boolean => {
  return !!getAnalyticsTagsForFacility(facilityId).pixelId;
};

export const canSendToGTM = (facilityId: string): boolean => {
  return !!getAnalyticsTagsForFacility(facilityId).GTM;
};
